import React, {useEffect} from 'react';
import {FormControl, Grid, Typography} from '@mui/material';
import {numericFormatter} from 'react-number-format';
import {useAccountContext} from '../../../contexts/AccountContext';
import {
  ActionType,
  AutoField,
  AutoFieldProps,
  AutoTextField,
  SelectField,
} from '../../../components/AutoForm/AutoFieldNew';
import Loading from '../../../components/Loading/Loading';
import {useRecommendContext} from './CreateRecommendHelpers';
import {useAutoField} from '../../../components/AutoForm/useAutoForm';
import {UPDATES} from '../../../shared/UPDATES';

export const ongoingFeeField: SelectField = {
  id: 'ongoingFee',
  label: 'Ongoing fee',
  type: ActionType.select,
  options: [
    {
      label: '0.00%',
      value: '0.00',
    },
    {
      label: '0.05%',
      value: '0.05',
    },
    {
      label: '0.10%',
      value: '0.10',
    },
    {
      label: '0.15%',
      value: '0.15',
    },
    {
      label: '0.20%',
      value: '0.20',
    },
    {
      label: '0.25%',
      value: '0.25',
    },
    {
      label: '0.30%',
      value: '0.30',
    },
    {
      label: '0.35%',
      value: '0.35',
    },
    {
      label: '0.40%',
      value: '0.40',
    },
    {
      label: '0.45%',
      value: '0.45',
    },
    {
      label: '0.50%',
      value: '0.50',
    },
    {
      label: '0.55%',
      value: '0.55',
    },
    {
      label: '0.60%',
      value: '0.60',
    },
    {
      label: '0.65%',
      value: '0.65',
    },
    {
      label: '0.70%',
      value: '0.70',
    },
    {
      label: '0.75%',
      value: '0.75',
    },
    {
      label: '0.80%',
      value: '0.80',
    },
    {
      label: '0.85%',
      value: '0.85',
    },
    {
      label: '0.90%',
      value: '0.90',
    },
    {
      label: '0.95%',
      value: '0.95',
    },
    {
      label: '1.00%',
      value: '1.00',
    },
  ],
};

export const getFeeFormikFromServer = (
  serverStr?: string | null
): {feeType: 'GBP' | '%'; fixedFee: string; percentageFee: string; fee: string} => {
  if (!serverStr || !serverStr.length) {
    return {feeType: '%', fixedFee: '', percentageFee: '', fee: '0'};
  }
  const hasGbp = serverStr.includes('GBP');
  if (hasGbp) {
    const value = serverStr.replace('GBP', '').trim();
    return {
      feeType: 'GBP',
      fixedFee: numericFormatter(value, {decimalScale: 2, fixedDecimalScale: true}),
      percentageFee: '',
      fee: numericFormatter(value, {decimalScale: 2}),
    };
  }
  const value = serverStr.replace('%', '').trim();
  return {
    feeType: '%',
    percentageFee: Number(value).toFixed(2),
    fixedFee: '',
    fee: value,
  };
};

export const OngoingFeeField: React.FC<
  {
    formikKeyPrefixWithoutFee: string;
  } & Partial<AutoFieldProps>
> = ({formikKeyPrefixWithoutFee: formikKeyPrefix, disabled, helperText}) => {
  // How this works:
  // the fee field is now JUST used for loading up server values into the form.
  // Then this is converted into either fixedFee or percentageFee
  // The frontend then needs to convert them back to the values.
  const ongoingFeeKey = `${formikKeyPrefix}.percentageFee`;
  const {account} = useAccountContext();
  const [fee, feeMeta, feeHelpers] = useAutoField<string | undefined>(ongoingFeeKey);
  const {recommendDetails} = useRecommendContext();
  useEffect(() => {
    if (!account) {
      return;
    }
    if (recommendDetails) {
      return; // if recommend exists, dont default value this
    }
    if (disabled) {
      // No Ongoing Fee on New LISA
      feeHelpers.setValue(UPDATES._4017, undefined, true);
      return;
    }
    if (!fee.value?.length) {
      if (account.ongoingFee) {
        feeHelpers.setValue(UPDATES._4018, `${account.ongoingFee}`, true);
      }
    }
  }, [recommendDetails, account?.ongoingFee, formikKeyPrefix, fee.value, disabled]);

  if (!account) {
    return (
      <Grid container spacing={2}>
        <Loading entityName="fees" />
      </Grid>
    );
  }

  return (
    <>
      <FormControl fullWidth variant="filled" size="small">
        <AutoField field={{...ongoingFeeField, id: ongoingFeeKey}} disabled={disabled} />
        {helperText?.length && <Typography variant="caption">{helperText}</Typography>}
      </FormControl>
    </>
  );
};
